// Toast.tsx — Transient feedback notification for admin actions.
//
// Auto-dismisses after 4 seconds. Stacks above all other UI (z-index: 200).
// Two states: ok (gold) and err (gold-m — muted amber, not "error red").
// Brand Bible Ch 9: no alarming colors, no exclamation marks.

import React, { useEffect } from 'react';

export type ToastKind = 'ok' | 'err';

export interface ToastMessage {
  id: string;
  kind: ToastKind;
  text: string;
}

interface ToastProps {
  message: ToastMessage;
  onDismiss: (id: string) => void;
}

const AUTO_DISMISS_MS = 4_000;

export default function Toast({ message, onDismiss }: ToastProps): React.ReactElement {
  useEffect(() => {
    const timer = setTimeout(() => { onDismiss(message.id); }, AUTO_DISMISS_MS);
    return () => clearTimeout(timer);
  }, [message.id, onDismiss]);

  return (
    <div
      className={`toast toast-${message.kind}`}
      role="status"
      aria-live="polite"
      onClick={() => onDismiss(message.id)}
    >
      {message.text}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ToastHost — renders the toast stack; placed once near the app root.
// ---------------------------------------------------------------------------

interface ToastHostProps {
  messages: ToastMessage[];
  onDismiss: (id: string) => void;
}

export function ToastHost({ messages, onDismiss }: ToastHostProps): React.ReactElement | null {
  if (messages.length === 0) return null;
  return (
    <div className="toast-host" aria-live="polite" aria-atomic="false">
      {messages.map((m) => (
        <Toast key={m.id} message={m} onDismiss={onDismiss} />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// useToast — lightweight hook, no external state management needed.
// ---------------------------------------------------------------------------

import { useCallback, useState } from 'react';

export interface UseToastReturn {
  messages: ToastMessage[];
  toast: (kind: ToastKind, text: string) => void;
  dismiss: (id: string) => void;
}

export function useToast(): UseToastReturn {
  const [messages, setMessages] = useState<ToastMessage[]>([]);

  const toast = useCallback((kind: ToastKind, text: string) => {
    const id = `${Date.now()}-${kind}`;
    setMessages((prev) => [...prev, { id, kind, text }]);
  }, []);

  const dismiss = useCallback((id: string) => {
    setMessages((prev) => prev.filter((m) => m.id !== id));
  }, []);

  return { messages, toast, dismiss };
}
